/* eslint-disable react-hooks/exhaustive-deps */
/**
 * @author        yontaek@zumper.com (Yon Taek Chung)
 * @copyright     Copyright (c) 2022, Zumper
 * @description   Save search button container
 */

import { checkLoggedIn, getUser } from '@zcode-ui/web-zumper-global-state'
import { localStorageSingleton, trackGAEvent } from '@zcode/nr-utils'
import { createFilterCopy } from '@zcode/nr/es/shared/utils/save_search'
import type { Filters, SaveSearchModal } from '@zcode/state-type'
import type { MaybeEmpty } from '@zcode/utility-types'
import deepequal from 'fast-deep-equal'
import React, { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { logger } from '../../logger'
import { updateSearchUI } from '../../module/actions'
import { API_RESPONSE, NEARBY_SEARCH } from '../../module/constants'
import {
  checkSaveSearchCreateView,
  getCurrentLocation,
  getCurrentLocationForSaveSearch,
  selectFilters,
  selectSavedSearches,
} from '../../module/selectors'
import { createComparableSearchQuery } from '../../utils'
import { SaveSearchButtonMobile } from './SaveSearchButtonMobile'

export interface SaveSearchButtonContainerProps {
  dispatchCreateSearch: (args: {
    isLoggedIn: boolean
    origin: `f-popup-${string}`
    type: typeof NEARBY_SEARCH
    email: string
    filters: MaybeEmpty<Filters>
  }) => Promise<any>
  dispatchRemoveSavedSearch: (searchId: number) => void
  isDetailView?: boolean
  isMobile?: boolean
  onSaveSearchModalOpen: (arg: {
    filters: Filters
    origin: Exclude<SaveSearchModal['origin'], ''>
  }) => void
  origin: `f-popup-${string}`
}

export const SaveSearchButtonMobileContainer = ({
  dispatchCreateSearch,
  dispatchRemoveSavedSearch,
  isDetailView,
  isMobile,
  onSaveSearchModalOpen,
  origin,
}: SaveSearchButtonContainerProps) => {
  const dispatch = useDispatch()
  const filters = useSelector(selectFilters)
  const isLoggedIn = useSelector(checkLoggedIn)
  const isSaveSearchCreateView = useSelector(checkSaveSearchCreateView)
  const location = useSelector(getCurrentLocation)
  const saveSearchLocation = useSelector(getCurrentLocationForSaveSearch)
  const user = useSelector(getUser)
  const savedSearches = useSelector(selectSavedSearches)

  const loc = isSaveSearchCreateView ? saveSearchLocation : location
  const filterLocCopy = createFilterCopy(filters, loc)

  const isSavedSearch = useMemo(() => {
    const currentFilters = createComparableSearchQuery({
      filters: filterLocCopy,
    })

    if (savedSearches?.length) {
      return savedSearches.some((savedSearch) => {
        const {
          query: { origin, maxLat, maxLng, minLat, minLng, ...query },
        } = savedSearch ?? {}

        return deepequal(currentFilters, query)
      })
    }

    return false
  }, [JSON.stringify(filters), JSON.stringify(loc), JSON.stringify(savedSearches)])

  const removeSearch = () => {
    const currentFilters = createComparableSearchQuery({
      filters: filterLocCopy,
    })

    if (savedSearches?.length) {
      savedSearches.some((savedSearch) => {
        const {
          query: { origin, maxLat, maxLng, minLat, minLng, ...query },
          searchId,
        } = savedSearch ?? {}

        if (deepequal(currentFilters, query) && searchId) {
          dispatchRemoveSavedSearch(searchId)
          return true
        }

        return false
      })
    }
  }

  const saveSearchHelper = () => {
    let filterCopy: MaybeEmpty<Filters> = {}

    if (user.email) {
      if (filters) {
        filterCopy = filterLocCopy
      }

      dispatchCreateSearch({
        isLoggedIn,
        origin,
        type: NEARBY_SEARCH,
        email: user.email,
        filters: filterCopy,
      })
        .then(() => {
          // Success, disallow creating searches for this user
          localStorageSingleton.set('preventCreateAlertFromMsg', true)
          dispatch(
            updateSearchUI({
              key: 'searchView',
              value: 'main',
            })
          )
        })
        .catch((error) => {
          const errData = error.data || {}
          // Failure, could be for a number of reasons but we'll disable the checkbox for now
          if (errData.reason === API_RESPONSE.OTHER_USER) {
            logger.error(
              'SaveSearchButton, dispatchCreateSearch failed (probably an existing user)',
              error
            )
          }
        })
    } else {
      onSaveSearchModalOpen({
        origin,
        filters,
      })
    }
  }

  const saveSearch = () => {
    trackGAEvent('Alerts', 'save-search-click', origin, user.email ? '1' : '0')
    if (isMobile && (isLoggedIn || user.email)) {
      saveSearchHelper()
    } else {
      onSaveSearchModalOpen({
        filters,
        origin,
      })
    }
  }

  const onClick = () => {
    if (isDetailView) {
      onSaveSearchModalOpen({
        filters,
        origin,
      })
    } else {
      if (isSavedSearch) {
        removeSearch()
      } else {
        saveSearch()
      }
    }
  }

  return <SaveSearchButtonMobile isSavedSearch={isSavedSearch} onClick={onClick} />
}
